import { z } from '../../renderer/src/schema-library.js';
import { MapStyleSchema } from '../../renderer/src/map-style-contract.js';
import { CameraPlatformSchema } from '../../renderer/src/camera-platform-contract.js';
import { CameraGimbalSchema } from '../../renderer/src/camera-gimbal-contract.js';
import { CameraAngleSchema } from '../../renderer/src/camera-angle-contract.js';
import { DepthOfFieldSchema } from '../../renderer/src/depth-of-field-contract.js';
import { TrackAppearanceSchema } from '../../renderer/src/track-appearance-contract.js';
import { VideoPresentationSchema } from '../../renderer/src/presentation-contract.js';
import { ExportRangeSchema, validExportTiming, EXPORT_RANGE_ERROR } from '../../renderer/src/export-range-contract.js';
import { CinematicSequenceSchema, validCameraTrackTiming, CAMERA_TRACK_TIMING_ERROR } from '../../renderer/src/cinematic-sequence-contract.js';
import { PlaceTitleSchema } from '../../renderer/src/place-title.js';
import { CAMERA_DISTANCE_IDS, ROUTE_LINE_STYLE_IDS, resolveOutput, MIN_RENDER_DURATION_SECONDS, MAX_RENDER_DURATION_SECONDS } from '../../renderer/src/presets.js';
import { presentationSupportsPreset } from '../../renderer/src/video-presentation.js';

/** Editable settings shared by project storage and render admission. */
export const StudioSettingsObject = z.object({
  preset: z.enum(['landscape-16x9', 'social-4x5', 'vertical-9x16']),
  durationSeconds: z.int().min(MIN_RENDER_DURATION_SECONDS).max(MAX_RENDER_DURATION_SECONDS),
  cameraStyle: CameraPlatformSchema, cameraStyleVersion: z.union([z.literal(3), z.literal(4)]).default(4),
  cameraDistance: z.enum(CAMERA_DISTANCE_IDS), lineStyle: z.enum(ROUTE_LINE_STYLE_IDS).default('pipe'),
  cameraGimbal: CameraGimbalSchema.optional(), cameraAngle: CameraAngleSchema.optional(),
  mapStyle: MapStyleSchema.optional(), terrainExaggeration: z.number().min(1).max(3).optional(),
  hidePlaces: z.boolean().optional(), unitSystem: z.enum(['metric', 'imperial']).optional(),
  placeTitle: PlaceTitleSchema.optional(), depthOfField: DepthOfFieldSchema.optional(),
  trackAppearance: TrackAppearanceSchema.optional(), presentation: VideoPresentationSchema.optional(),
  exportRange: ExportRangeSchema.optional(), cinematicSequence: CinematicSequenceSchema.optional(),
}).strict();
export const StudioSettingsSchema = StudioSettingsObject
  .refine(validExportTiming, EXPORT_RANGE_ERROR).refine(validCameraTrackTiming, CAMERA_TRACK_TIMING_ERROR)
  .refine(value => !value.presentation || presentationSupportsPreset(value.presentation, value.preset), 'This placement does not support the selected aspect ratio.');
export type StudioSettings = z.infer<typeof StudioSettingsSchema>;
export function compileStudioOutput(settings: StudioSettings) {
  const value = StudioSettingsSchema.parse(settings);
  return { ...value, ...resolveOutput(value.preset, value.durationSeconds, value.cameraStyle, value.cameraDistance, value.lineStyle, value.cameraStyleVersion), mapStyle: value.mapStyle ?? 'mcnally' };
}
export function defaultStudioSettings(): StudioSettings {
  return { preset: 'landscape-16x9', durationSeconds: 60, cameraStyle: 'aerial-follow', cameraStyleVersion: 4,
    cameraDistance: 'standard', cameraGimbal: 'glide', lineStyle: 'pipe', mapStyle: 'mcnally', unitSystem: 'metric', terrainExaggeration: 1.5, hidePlaces: false };
}

export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return '[' + value.map(canonicalJson).join(',') + ']';
  if (value && typeof value === 'object') return '{' + Object.entries(value).filter(([, item]) => item !== undefined)
    .sort(([a], [b]) => a.localeCompare(b)).map(([key, item]) => JSON.stringify(key) + ':' + canonicalJson(item)).join(',') + '}';
  return JSON.stringify(value);
}
export async function studioHash(value: unknown): Promise<string> {
  return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonicalJson(value)))))
    .map(byte => byte.toString(16).padStart(2, '0')).join('');
}
